import { UserIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import Pagination from "../../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../../components/ui/dashboard/Table";
import { buildApiUrl, formatDate } from "../../../lib/utils";
import {
  Maintenance,
  MaintenanceHistoryRequest,
  MaintenanceSearchResponse,
} from "../@types";
import { useGetMaintenanceHistoryMutation } from "../maintenanceApiSlice";

interface VehicleMaintenanceHistoryProps {
  vehicleId: string;
}

const VehicleMaintenanceHistory = ({
  vehicleId,
}: VehicleMaintenanceHistoryProps) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // RTK Query mutation
  const [getMaintenanceHistory, { data: maintenanceData, isLoading }] =
    useGetMaintenanceHistoryMutation();

  const fetchMaintenance = useCallback(async () => {
    const requestBody: MaintenanceHistoryRequest = {
      pageNumber: currentPage,
      pageSize: pageSize,
      vehicleId: vehicleId,
    };

    try {
      await getMaintenanceHistory(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch maintenances history:", err);
    }
  }, [currentPage, pageSize, getMaintenanceHistory]);

  // Fetch maintenances on component mount and when filters change
  useEffect(() => {
    fetchMaintenance();
  }, [currentPage, pageSize, fetchMaintenance]);

  // Table columns configuration
  const columns: Column<Maintenance>[] = [
    {
      key: "cardCode",
      header: "Card ID",
      width: "24",
      render: (value: string) => (
        <span className="text-hColor capitalize">{value}</span>
      ),
    },
    {
      key: "driverName",
      header: "Driver",
      width: "",
      render: (value: string, item: Maintenance) => (
        <div className="flex items-center space-x-3">
          {item.driverImage ? (
            <img
              src={buildApiUrl(item.driverImage)}
              alt={`${item.driverName}`}
              className="w-[30px] h-[30px] rounded-full object-cover border border-gray-300 shadow-sm"
            />
          ) : (
            <div className="w-[30px] h-[30px] rounded-full bg-gray-200 flex items-center justify-center border border-gray-300 shadow-sm p-1">
              <UserIcon className="w-full h-full text-hColor" />
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-hColor capitalize">{item.driver}</span>
          </div>
        </div>
      ),
    },

    {
      key: "vehicle",
      header: "Vehicle",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "maintenanceType",
      header: "Maintenance type",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "odometerReading",
      header: "Odometer reading",
      width: "16",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "20",
      render: (value: boolean, item: Maintenance) => {
        const getStatusInfo = () => {
          if (!value)
            return {
              label: "Inactive",
              color: "text-gray-200 border border-gray-200",
            };
          if (item.status === "Completed")
            return {
              label: "Completed",
              color: "text-green-800 border border-green-800",
            };
          if (item.status === "Pending")
            return {
              label: "Pending",
              color: "text-yellow-800 border border-yellow-800",
            };
          return {
            label: "Completed",
            color: "text-green-800 border border-green-800",
          };
        };

        const status = getStatusInfo();
        return (
          <span
            className={`px-2 py-1 inline-flex items-center justify-center text-xs font-semibold rounded-full ${status.color}`}
          >
            {status.label}
          </span>
        );
      },
    },
    {
      key: "createdOn",
      header: "Date",
      width: "20",
      render: (value: string) => (
        <span className="text-hColor">{formatDate(value)}</span>
      ),
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const response = maintenanceData?.data as
    | MaintenanceSearchResponse
    | undefined;

  return (
    <div className="bg-white rounded-[10px] p-4">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-xl font-semibold text-hColor">
          Maintenance history
        </h2>
      </div>

      {/* Table */}
      <div className="overflow-hidden space-y-6">
        <Table
          data={response?.data || []}
          columns={columns}
          loading={isLoading}
          emptyMessage="No maintenances found"
        />

        {/* Pagination */}
        {response && (
          <Pagination
            currentPage={response.currentPage}
            totalPages={response.totalPages}
            pageSize={response.pageSize}
            totalCount={response.totalCount}
            hasNextPage={response.hasNextPage}
            hasPreviousPage={response.hasPreviousPage}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>
    </div>
  );
};

export default VehicleMaintenanceHistory;
